import { BarcodeFormat, DecodeHintType } from '@zxing/library';

export type ScannerMode = 'barcode' | 'qr';

export const BARCODE_DETECTOR_FORMATS = [
  'ean_13',
  'ean_8',
  'upc_a',
  'upc_e',
  'code_128',
  'code_39',
  'itf',
] as const;

export const QR_DETECTOR_FORMATS = ['qr_code'] as const;

export const BARCODE_FRAME_SHELL = {
  aspectRatio: '4 / 3',
  backgroundColor: '#000',
  overflow: 'hidden',
  position: 'relative',
  width: '100%',
} as const;

export const QR_FRAME_SHELL = {
  aspectRatio: '1 / 1',
  backgroundColor: '#000',
  overflow: 'hidden',
  position: 'relative',
  width: '100%',
} as const;

export const SCANNER_VIDEO_STYLE = {
  display: 'block',
  height: '100%',
  left: 0,
  objectFit: 'cover',
  opacity: 0,
  position: 'absolute',
  top: 0,
  width: '100%',
} as const;

export const SCANNER_CANVAS_STYLE = {
  display: 'block',
  height: '100%',
  left: 0,
  position: 'absolute',
  top: 0,
  width: '100%',
} as const;

export const BARCODE_ZXING_FORMATS = [
  BarcodeFormat.EAN_13,
  BarcodeFormat.EAN_8,
  BarcodeFormat.UPC_A,
  BarcodeFormat.UPC_E,
  BarcodeFormat.CODE_128,
  BarcodeFormat.CODE_39,
  BarcodeFormat.ITF,
] as const;

export function detectorFormats(mode: ScannerMode): readonly string[] {
  return mode === 'qr' ? QR_DETECTOR_FORMATS : BARCODE_DETECTOR_FORMATS;
}

export function zxingFormats(mode: ScannerMode): readonly BarcodeFormat[] {
  return mode === 'qr' ? [BarcodeFormat.QR_CODE] : BARCODE_ZXING_FORMATS;
}

export function zxingHints(mode: ScannerMode): Map<DecodeHintType, unknown> {
  const hints = new Map<DecodeHintType, unknown>();
  hints.set(DecodeHintType.POSSIBLE_FORMATS, [...zxingFormats(mode)]);
  return hints;
}

export function acceptsDetectorCode(mode: ScannerMode, format: string | undefined): boolean {
  return Boolean(format && detectorFormats(mode).includes(format));
}

export function acceptsZxingFormat(mode: ScannerMode, format: BarcodeFormat): boolean {
  return zxingFormats(mode).includes(format);
}

export function coverBox(
  shellWidth: number,
  shellHeight: number,
  videoWidth: number,
  videoHeight: number,
): { height: number; left: number; top: number; width: number } | null {
  if (shellWidth <= 0 || shellHeight <= 0 || videoWidth <= 0 || videoHeight <= 0) return null;

  const scale = Math.max(shellWidth / videoWidth, shellHeight / videoHeight);
  const width = Math.ceil(videoWidth * scale);
  const height = Math.ceil(videoHeight * scale);

  return {
    height,
    left: Math.round((shellWidth - width) / 2),
    top: Math.round((shellHeight - height) / 2),
    width,
  };
}

export function paintVideoCover(video: HTMLVideoElement, canvas: HTMLCanvasElement): void {
  const shell = canvas.parentElement;
  if (!shell) return;

  const shellWidth = shell.clientWidth;
  const shellHeight = shell.clientHeight;
  const box = coverBox(shellWidth, shellHeight, video.videoWidth, video.videoHeight);
  if (!box) return;

  const dpr = window.devicePixelRatio || 1;
  const pixelWidth = Math.max(1, Math.round(shellWidth * dpr));
  const pixelHeight = Math.max(1, Math.round(shellHeight * dpr));
  if (canvas.width !== pixelWidth || canvas.height !== pixelHeight) {
    canvas.width = pixelWidth;
    canvas.height = pixelHeight;
  }

  const context = canvas.getContext('2d');
  if (!context) return;
  context.setTransform(dpr, 0, 0, dpr, 0, 0);
  context.drawImage(video, box.left, box.top, box.width, box.height);
}
